import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone,
  ViewChild,
  ElementRef,
} from '@angular/core';
import type { VgApiService } from '@videogular/ngx-videogular/core';
import { Subscription } from 'rxjs';
import { SensorRecord } from 'src/app/models/sensorRecord.model';
import { VideoResource } from 'src/app/models/videoResource.model';
import { DateFormatService } from 'src/app/services/dateFormat.service';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import type { ChartPlotBounds } from '../node-analytics-chart/node-analytics-chart.component';
import { DateTimeRange } from 'src/app/models/dateRange.model';

export type SyncPanelState = 'hidden' | 'video-only' | 'map-only' | 'video-map';

interface ClipBand {
  leftPct: number;
  widthPct: number;
  label: string;
}

type TimelineRecord = Record<string, unknown>;

@Component({
  standalone: false,
  selector: 'app-node-sync-panel',
  templateUrl: './node-sync-panel.component.html',
  styleUrls: ['./node-sync-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NodeSyncPanelComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('scrubberTrack') scrubberTrack?: ElementRef<HTMLElement>;

  @Input() videoResources: VideoResource[] = [];
  @Input() sensorRecords: unknown[] = [];
  @Input() isMobileSensor = false;
  @Input() timeCursor = 0;
  @Input() zoomRange: DateTimeRange | null = null;
  @Input() chartPlotBounds: ChartPlotBounds | null = null;

  @Output() timeCursorChange = new EventEmitter<number>();
  @Output() zoomOut = new EventEmitter<void>();

  readonly trackByIndex = trackByIndex;

  state: SyncPanelState = 'hidden';
  bodyStateClass: 'video-only' | 'map-only' | 'video-map' = 'video-only';
  panelSizeClass: 'sm' | 'md' | 'lg' = 'md';
  hasVideoData = false;
  hasMapData = false;
  currentVideo: VideoResource | null = null;
  sortedVideoResources: VideoResource[] = [];
  mapSensorRecords: SensorRecord[] = [];
  isPlaying = false;
  hasNextClip = false;
  volume = 1;
  isMuted = false;
  videoElapsedDisplay = '--:--';
  videoDurationDisplay = '--:--';

  clipBands: ClipBand[] = [];
  needleLeftPercent = 0;
  fillWidthPercent = 0;
  sparklinePolylinePoints = '';
  sparklinePolygonPoints = '';
  sparklineViewBox = '0 0 100 100';
  axisStart = '';
  axisEnd = '';
  clipIndexLabel = '';
  currentTimestampDisplay = '--:--:--';
  scrubberInsetLeftPx = 0;
  scrubberInsetRightPx = 0;

  private vgApi: VgApiService | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private timelineStart = 0;
  private timelineEnd = 0;
  private isVideoEmitting = false;
  private lastEmittedTs = 0;
  private lastNativeTimeUpdate = 0;
  private contentModeOverride: 'video-only' | 'map-only' | 'video-map' | null = null;
  private naturalBodyState: 'video-only' | 'map-only' | 'video-map' = 'video-only';
  private sparklineRecords: Array<{ timestamp: number; value: number }> = [];
  private isScrubbing = false;
  private playAfterScrub = false;
  private seekTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private dateFormatService: DateFormatService,
  ) {}

  ngOnInit(): void {
    this.updateDisplayState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dataChanged = changes['videoResources'] || changes['sensorRecords'];
    if (dataChanged) {
      this.contentModeOverride = null;
      this.updateDisplayState();
      return;
    }
    if (changes['zoomRange']) {
      this.onZoomRangeChange();
    }
    if (changes['chartPlotBounds']) {
      this.updateScrubberTrackInsets();
    }
    if (changes['timeCursor'] && !changes['timeCursor'].firstChange && !this.isVideoEmitting) {
      this.updateTimeCursor(this.timeCursor);
      this.updateScrubberPosition();
    }
  }

  ngOnDestroy(): void {
    this.detachVideoElement();
    this.vgApi = null;
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    if (this.seekTimeoutId) clearTimeout(this.seekTimeoutId);
  }

  onVideoReady(api: VgApiService): void {
    this.vgApi = api;
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.subscriptions.length = 0;
    this.subscriptions.push(
      api.subscriptions.play.subscribe(() => { this.isPlaying = true; this.cdr.markForCheck(); }),
      api.subscriptions.pause.subscribe(() => { this.isPlaying = false; this.cdr.markForCheck(); }),
      api.getDefaultMedia().subscriptions.ended.subscribe(() => this.onVideoEnded())
    );
    api.volume = this.volume;
    const mediaEl = api.getDefaultMedia()?.elem as HTMLVideoElement | null;
    if (mediaEl) this.attachVideoElement(mediaEl);
  }

  setSyncSize(size: 'sm' | 'md' | 'lg'): void {
    this.panelSizeClass = size;
    this.cdr.markForCheck();
  }

  setContentMode(mode: 'video-only' | 'map-only' | 'video-map'): void {
    if (!this.isContentModeAvailable(mode)) {
      return;
    }
    if (mode === 'map-only' && this.isPlaying) {
      this.vgApi?.pause();
      this.isPlaying = false;
    }
    this.contentModeOverride = mode;
    this.bodyStateClass = mode;
    this.cdr.markForCheck();
  }

  isContentModeAvailable(mode: 'video-only' | 'map-only' | 'video-map'): boolean {
    if (mode === 'video-only') {
      return this.hasVideoData;
    }
    if (mode === 'map-only') {
      return this.hasMapData;
    }
    return this.hasVideoData && this.hasMapData;
  }

  togglePlayPause(): void {
    if (!this.vgApi) return;
    if (this.isPlaying) this.vgApi.pause();
    else this.vgApi.play();
  }

  skipToNext(): void {
    if (!this.currentVideo) return;
    const inRange = this.getVideosInRange();
    const idx = inRange.indexOf(this.currentVideo);
    if (idx >= 0 && idx < inRange.length - 1) {
      this.currentVideo = inRange[idx + 1];
      this.updateVideoInfoDisplay();
      this.cdr.markForCheck();
      if (this.seekTimeoutId) clearTimeout(this.seekTimeoutId);
      this.seekTimeoutId = setTimeout(() => { if (this.vgApi && this.isPlaying) this.vgApi.play(); }, 200);
    }
  }

  setVolume(event: Event): void {
    const value = Number((event.target as HTMLInputElement).value);
    this.volume = value;
    this.isMuted = value === 0;
    if (this.vgApi) this.vgApi.volume = value;
  }

  toggleMute(): void {
    this.isMuted = !this.isMuted;
    const applied = this.isMuted ? 0 : (this.volume || 1);
    if (this.vgApi) this.vgApi.volume = applied;
    if (!this.isMuted && this.volume === 0) this.volume = 1;
  }

  requestZoomOut(): void {
    this.zoomOut.emit();
  }

  onScrubberMouseDown(event: MouseEvent): void {
    this.isScrubbing = true;
    this.playAfterScrub = this.isPlaying;
    if (this.isPlaying) this.vgApi?.pause();
    this.scrubTo(event);
  }

  onScrubberMouseMove(event: MouseEvent): void {
    if (!this.isScrubbing) return;
    this.scrubTo(event);
  }

  onScrubberMouseUp(event: MouseEvent): void {
    if (!this.isScrubbing) return;
    this.isScrubbing = false;
    this.scrubTo(event);
    if (this.playAfterScrub) setTimeout(() => this.vgApi?.play(), 100);
  }

  onScrubberMouseLeave(): void {
    if (!this.isScrubbing) return;
    this.isScrubbing = false;
    if (this.playAfterScrub) setTimeout(() => this.vgApi?.play(), 100);
  }

  private scrubTo(event: MouseEvent): void {
    const track = this.scrubberTrack?.nativeElement;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const eff = this.getEffectiveRange();
    const ts = eff.start + pct * (eff.end - eff.start);
    this.lastEmittedTs = ts;
    this.isVideoEmitting = true;
    this.timeCursorChange.emit(ts);
    this.isVideoEmitting = false;
    this.updateTimeCursor(ts);
    this.updateScrubberPosition(ts);
    this.cdr.markForCheck();
  }

  private attachVideoElement(el: HTMLVideoElement): void {
    this.detachVideoElement();
    this.videoElement = el;
    this.ngZone.runOutsideAngular(() => el.addEventListener('timeupdate', this.onNativeTimeUpdate));
  }

  private detachVideoElement(): void {
    if (this.videoElement) {
      this.videoElement.removeEventListener('timeupdate', this.onNativeTimeUpdate);
      this.videoElement = null;
    }
  }

  private readonly onNativeTimeUpdate = (): void => {
    const now = Date.now();
    if (now - this.lastNativeTimeUpdate < 200) return;
    this.lastNativeTimeUpdate = now;
    if (!this.currentVideo || !this.videoElement) return;
    const elapsed = this.videoElement.currentTime;
    const ts = this.currentVideo.timestampStart + elapsed * 1000;
    if (Math.abs(ts - this.lastEmittedTs) < 100) return;
    this.lastEmittedTs = ts;
    this.isVideoEmitting = true;
    this.ngZone.run(() => {
      this.timeCursorChange.emit(ts);
      this.updateScrubberPosition(ts);
      this.updateVideoTimeDisplay(elapsed);
      this.cdr.markForCheck();
    });
    this.isVideoEmitting = false;
  };

  private onVideoEnded(): void {
    const inRange = this.getVideosInRange();
    const idx = this.currentVideo ? inRange.indexOf(this.currentVideo) : -1;
    if (idx >= 0 && idx < inRange.length - 1) {
      this.currentVideo = inRange[idx + 1];
      this.updateVideoInfoDisplay();
      this.cdr.markForCheck();
      if (this.seekTimeoutId) clearTimeout(this.seekTimeoutId);
      this.seekTimeoutId = setTimeout(() => this.vgApi?.play(), 200);
    } else {
      this.isPlaying = false;
      this.cdr.markForCheck();
    }
  }

  private onZoomRangeChange(): void {
    if (this.isPlaying) {
      this.vgApi?.pause();
      this.isPlaying = false;
    }
    this.syncMapRecordsToZoom();
    if (!this.sortedVideoResources.length) return;
    const inRange = this.getVideosInRange();
    if (!inRange.length) {
      this.currentVideo = null;
      this.updateScrubberFields();
      this.updateVideoInfoDisplay();
      this.cdr.markForCheck();
      return;
    }
    if (!this.currentVideo || !inRange.includes(this.currentVideo)) {
      this.currentVideo = inRange[0];
      const seekTo = this.currentVideo.timestampStart;
      Promise.resolve().then(() => this.ngZone.run(() => {
        this.timeCursorChange.emit(seekTo);
        this.cdr.markForCheck();
      }));
    }
    this.updateScrubberFields();
    this.updateScrubberTrackInsets();
    this.updateVideoInfoDisplay();
    this.cdr.markForCheck();
  }

  private updateDisplayState(): void {
    const hasVideo = (this.videoResources?.length ?? 0) > 0;
    const hasMap = this.isMobileSensor && (this.sensorRecords ?? []).some(r => this.getRecordLatLon(r) != null);
    this.hasVideoData = hasVideo;
    this.hasMapData = hasMap;

    if (!hasVideo && !hasMap) {
      this.state = 'hidden';
      this.naturalBodyState = 'video-only';
    } else if (hasVideo && hasMap) {
      this.state = 'video-map';
      this.naturalBodyState = 'video-map';
    } else if (hasVideo) {
      this.state = 'video-only';
      this.naturalBodyState = 'video-only';
    } else {
      this.state = 'map-only';
      this.naturalBodyState = 'map-only';
    }

    this.bodyStateClass = this.contentModeOverride ?? this.naturalBodyState;

    this.sortedVideoResources = [...(this.videoResources ?? [])].sort(
      (a, b) => a.timestampStart - b.timestampStart
    );
    this.syncMapRecordsToZoom();

    this.extractTimeline();
    this.buildSparklineData();

    if (this.sortedVideoResources.length) {
      const inRange = this.getVideosInRange();
      if (!this.currentVideo || !inRange.includes(this.currentVideo)) {
        this.currentVideo = inRange[0] ?? null;
      }
    }

    this.updateScrubberFields();
    this.updateVideoInfoDisplay();
    this.cdr.markForCheck();
  }

  private updateScrubberFields(): void {
    this.buildClipBands();
    this.buildSparklinePoints();
    this.updateScrubberPosition();
  }

  private buildClipBands(): void {
    const eff = this.getEffectiveRange();
    const span = eff.end - eff.start;
    if (span <= 0) { this.clipBands = []; return; }
    const inRange = this.getVideosInRange();
    this.clipBands = inRange.map((v, i) => {
      const left = Math.max(0, (v.timestampStart - eff.start) / span * 100);
      const clampedStart = Math.max(v.timestampStart, eff.start);
      const clampedEnd = Math.min(v.timestampEnd, eff.end);
      const width = Math.max(0, (clampedEnd - clampedStart) / span * 100);
      return { leftPct: left, widthPct: width, label: `Clip ${i + 1} of ${inRange.length}` };
    });
  }

  private updateScrubberPosition(ts?: number): void {
    const cursor = ts ?? this.timeCursor;
    const eff = this.getEffectiveRange();
    const span = eff.end - eff.start;
    this.currentTimestampDisplay = this.formatTimestamp(cursor);
    if (span <= 0) {
      this.needleLeftPercent = 0;
      this.fillWidthPercent = 0;
      return;
    }
    const pct = Math.max(0, Math.min(100, (cursor - eff.start) / span * 100));
    this.needleLeftPercent = pct;
    this.fillWidthPercent = pct;
    this.axisStart = this.formatTimestamp(eff.start);
    this.axisEnd = this.formatTimestamp(eff.end);
  }

  private buildSparklineData(): void {
    this.sparklineRecords = (this.sensorRecords ?? [])
      .map((record) => {
        const timelineRecord = this.toTimelineRecord(record);
        const timestamp = Number(timelineRecord?.['timestamp']);
        const value = Number(timelineRecord?.['value'] ?? timelineRecord?.['reading'] ?? 0);
        return {
          timestamp,
          value,
        };
      })
      .filter((record) => Number.isFinite(record.timestamp) && Number.isFinite(record.value) && record.timestamp > 0);
  }

  private buildSparklinePoints(): void {
    const eff = this.getEffectiveRange();
    const span = eff.end - eff.start;
    if (span <= 0) {
      this.sparklinePolylinePoints = '';
      this.sparklinePolygonPoints = '';
      this.sparklineViewBox = '0 0 100 100';
      return;
    }
    const inRange = this.sparklineRecords.filter(r => r.timestamp >= eff.start && r.timestamp <= eff.end);
    if (inRange.length < 2) {
      this.sparklinePolylinePoints = '';
      this.sparklinePolygonPoints = '';
      this.sparklineViewBox = '0 0 100 100';
      return;
    }
    const MAX_POINTS = 150;
    const step = inRange.length > MAX_POINTS ? Math.ceil(inRange.length / MAX_POINTS) : 1;
    const sampled = step > 1 ? inRange.filter((_, i) => i % step === 0) : inRange;
    const vals = sampled.map(r => r.value);
    const minVal = vals.reduce((min, v) => (v < min ? v : min), vals[0]);
    const maxVal = vals.reduce((max, v) => (v > max ? v : max), vals[0]);
    const valSpan = maxVal - minVal || 1;
    const pts = sampled.map(r => {
      const x = (r.timestamp - eff.start) / span * 100;
      const y = 100 - ((r.value - minVal) / valSpan * 100);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    });
    this.sparklinePolylinePoints = pts.join(' ');
    this.sparklinePolygonPoints = `0,100 ${pts.join(' ')} 100,100`;
    this.sparklineViewBox = '0 0 100 100';
  }

  private updateVideoInfoDisplay(): void {
    if (!this.currentVideo) {
      this.clipIndexLabel = '';
      this.hasNextClip = false;
      this.videoElapsedDisplay = '--:--';
      this.videoDurationDisplay = '--:--';
      return;
    }
    const inRange = this.getVideosInRange();
    const idx = inRange.indexOf(this.currentVideo);
    this.clipIndexLabel = inRange.length > 1 ? `${idx + 1}/${inRange.length}` : '';
    this.hasNextClip = idx >= 0 && idx < inRange.length - 1;
    const duration = (this.currentVideo.timestampEnd - this.currentVideo.timestampStart) / 1000;
    this.videoDurationDisplay = this.formatMMSS(duration);
    const elapsed = Math.max(0, (this.timeCursor - this.currentVideo.timestampStart) / 1000);
    this.videoElapsedDisplay = this.formatMMSS(Math.min(elapsed, duration));
  }

  private updateVideoTimeDisplay(elapsedSeconds: number): void {
    if (!this.currentVideo) return;
    const duration = (this.currentVideo.timestampEnd - this.currentVideo.timestampStart) / 1000;
    this.videoElapsedDisplay = this.formatMMSS(Math.min(elapsedSeconds, duration));
  }

  private formatMMSS(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  private updateTimeCursor(ts: number): void {
    if (Math.abs(ts - this.lastEmittedTs) < 100) return;
    const newVideo = this.getVideoForTs(ts);
    if (newVideo && newVideo !== this.currentVideo) {
      this.currentVideo = newVideo;
      this.updateVideoInfoDisplay();
      this.cdr.markForCheck();
    }
    if (this.vgApi && this.currentVideo && !this.isVideoEmitting) {
      const elapsed = (ts - this.currentVideo.timestampStart) / 1000;
      const current = (this.vgApi.currentTime as number) ?? 0;
      if (Math.abs(current - elapsed) > 0.5) {
        this.vgApi.currentTime = Math.max(0, elapsed);
      }
    }
  }

  private getVideosInRange(): VideoResource[] {
    const eff = this.getEffectiveRange();
    return this.sortedVideoResources.filter(
      v => v.timestampEnd >= eff.start && v.timestampStart <= eff.end
    );
  }

  private getEffectiveRange(): DateTimeRange {
    const start = this.zoomRange?.start ?? this.timelineStart;
    const end = this.zoomRange?.end ?? this.timelineEnd;
    return {
      start: Number.isFinite(start) ? start : 0,
      end: Number.isFinite(end) ? end : 0,
    };
  }

  private getVideoForTs(ts: number): VideoResource | null {
    return this.sortedVideoResources.find(v => ts >= v.timestampStart && ts <= v.timestampEnd) ?? null;
  }

  private getRecordLatLon(record: unknown): { lat: number; lon: number } | null {
    if (!record || typeof record !== 'object') return null;
    const r = record as Record<string, unknown>;
    const lat = Number(r['lat'] ?? r['latitude'] ?? r['Latitude']);
    const lon = Number(r['lon'] ?? r['longitude'] ?? r['Longitude']);
    if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat === 0 || lon === 0 || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      return null;
    }

    return { lat, lon };
  }

  private extractTimeline(): void {
    const videoTs = this.sortedVideoResources.flatMap(v => [v.timestampStart, v.timestampEnd]);
    const recordTs = (this.sensorRecords ?? [])
      .map((record) => Number(this.toTimelineRecord(record)?.['timestamp']))
      .filter((timestamp) => Number.isFinite(timestamp) && timestamp > 0);
    const all = [...videoTs, ...recordTs].filter(t => t > 0);
    if (!all.length) { this.timelineStart = 0; this.timelineEnd = 0; return; }
    this.timelineStart = all.reduce((min, t) => (t < min ? t : min), all[0]);
    this.timelineEnd = all.reduce((max, t) => (t > max ? t : max), all[0]);
  }

  private toTimelineRecord(record: unknown): TimelineRecord | null {
    if (!record || typeof record !== 'object') {
      return null;
    }

    return record as TimelineRecord;
  }

  private syncMapRecordsToZoom(): void {
    const all = this.toMapSensorRecords(this.sensorRecords);
    if (!this.hasMapData) {
      this.mapSensorRecords = all;
      return;
    }
    const start = this.zoomRange?.start;
    const end = this.zoomRange?.end;
    if (start != null && end != null) {
      this.mapSensorRecords = all.filter(r => r.timestamp >= start && r.timestamp <= end);
    } else {
      this.mapSensorRecords = all;
    }
    this.cdr.markForCheck();
  }

  private toMapSensorRecords(records: unknown[]): SensorRecord[] {
    return records.filter((record): record is SensorRecord => {
      if (!record || typeof record !== 'object') {
        return false;
      }

      const timestamp = Number((record as TimelineRecord)['timestamp']);
      return Number.isFinite(timestamp) && timestamp > 0;
    });
  }

  private updateScrubberTrackInsets(): void {
    const horizontalPaddingPx = 12;

    if (!this.chartPlotBounds) {
      this.scrubberInsetLeftPx = 0;
      this.scrubberInsetRightPx = 0;
      this.cdr.markForCheck();
      return;
    }

    const left = Number.isFinite(this.chartPlotBounds.leftPx) ? this.chartPlotBounds.leftPx : 0;
    const right = Number.isFinite(this.chartPlotBounds.rightPx) ? this.chartPlotBounds.rightPx : 0;

    this.scrubberInsetLeftPx = Math.max(0, left - horizontalPaddingPx);
    this.scrubberInsetRightPx = Math.max(0, right - horizontalPaddingPx);
    this.cdr.markForCheck();
  }

  formatTimestamp(ts: number): string {
    if (!Number.isFinite(ts) || ts <= 0) return '--:--:--';
    return this.dateFormatService.formatTimestampToString(ts);
  }
}
